/** Types for the "create bank of changes from change appendices" workflow
 * (`create-bank-of-changes`). The wire types mirror the add-ins API contract. */

export type BankAttachment = {
  filename: string;
  content_base64: string;
  mime_type?: string;
};

export type BankSection = {
  key: string;
  title: string;
  existing_count: number;
};

export type BankAppendixSummary = {
  filename: string;
  paragraphs: number;
  tracked_changes: number;
};

/** A file the server keeps for the conversation: an upload, or a built version. */
export type BankStoredFile = {
  id: string;
  name: string;
  size: number;
};

/** A file of the conversation as the pane holds it: one the lawyer just picked
 * (still only in the pane), or one the server already keeps. */
export type BankUpload = File | BankStoredFile;

export function isStoredUpload(upload: BankUpload): upload is BankStoredFile {
  return typeof (upload as BankStoredFile).id === 'string';
}

/** The conversation as the server keeps it. Nothing of a conversation is kept
 * in the pane: this is where it goes on from, in Word or in the dashboard. */
export type BankConversationInfo = {
  id: string;
  step: 'awaiting_bank' | 'awaiting_appendices' | 'awaiting_confirmation' | 'done';
  version: number;
  appendix_count: number;
  /** A run of it (the look, the build, an answer) is going on in the server right now. */
  running: boolean;
  /** That run, as far as it got. It goes on in the server whether or not a window is open; any window follows it. */
  run: BankServerRun | null;
  /** A version exists and the agent can carry out an answer to it. */
  answerable: boolean;
  bank: BankStoredFile | null;
  appendices: BankStoredFile[];
  analysis: BankAnalysis | null;
};

/** A run that is going on in the server. `activity` is null for the look, which reports no stages. */
export type BankServerRun = {
  kind: 'analyze' | 'build' | 'revise';
  activity: BankActivity | null;
  /** The lawyer asked for it to stop, and it has not stopped yet. */
  stop_requested: boolean;
};

/** What the look at the bank found. */
export type BankAnalysis = {
  /** Hebrew, ready to show as the agent's turn. */
  assistant_message: string;
  /** Informational — `assistant_message` already describes them. */
  sections: BankSection[];
  appendices: BankAppendixSummary[];
  warnings: string[];
};

/** One generated .docx, kept on the server. */
export type BankGeneratedFile = {
  id: string;
  filename: string;
  size: number;
};

/** One version of the bank: the bank as built, and the same bank after the
 * refinement stage (numbering, openings, source notes). `refined` is null when
 * that stage could not be completed. */
export type BankVersionFiles = {
  version: number;
  built: BankGeneratedFile;
  refined: BankGeneratedFile | null;
};

/** A failure whose message is the server's own human-readable (Hebrew) text —
 * the runner shows it as the agent's reply as is. Every other failure
 * (network, timeout, malformed stream) is shown with the runner's own copy. */
export class BankOfChangesServerError extends Error {
  /** The HTTP status the reason came with, when it came with one (409: the conversation is being worked on). */
  readonly status?: number;

  constructor(detail: string, status?: number) {
    super(detail);
    this.name = 'BankOfChangesServerError';
    this.status = status;
  }
}

/** The server no longer takes the session's sign-in, and it could not be renewed
 * silently. The files and the conversation are still in the pane. */
export class BankOfChangesAuthError extends Error {
  constructor() {
    super('The sign-in expired and could not be renewed');
    this.name = 'BankOfChangesAuthError';
  }
}

export type BankRunnerStep =
  | 'awaiting_bank'
  | 'awaiting_appendices'
  | 'analyzing'
  | 'awaiting_confirmation'
  | 'building'
  /** The files are ready; the lawyer may answer the agent's follow-up or ask for a correction. */
  | 'done'
  | 'revising';

/** One generated file as a turn of the chat offers it. The file itself is on
 * the server, so every version stays downloadable from the history. */
export type BankOutputFile = {
  /** Unique in the conversation (`v2-refined`): tells apart the same kind of file of two versions. */
  key: string;
  /** `built`: the bank as built. `refined`: the same bank after the refinement stage. */
  kind: 'built' | 'refined';
  version: number;
  /** What the file is, as the title of its card ("הבנק אחרי ההתאמות"). */
  label: string;
  /** The small line under the title: the file type, and the version from the second one on. */
  detail: string;
  /** The accessible name of its download button: with several versions in the chat, each button says which file it takes. */
  downloadLabel: string;
  filename: string;
  /** The file's id in the conversation's files on the server. */
  id: string;
};

/** Everything the server reported during one stage of a run, in order. */
export type BankActivityCluster = {
  stage: string;
  lines: string[];
};

/** The progress of one build or one revision, stage by stage. While the run
 * goes on it is the live status; when it ends it stays in the transcript,
 * collapsed, so what was done can be opened and read. */
export type BankActivity = {
  kind: 'build' | 'revise';
  /** `stopped`: the lawyer stopped it, or left the conversation while it ran. */
  state: 'running' | 'done' | 'failed' | 'stopped';
  clusters: BankActivityCluster[];
};

export type BankChatTurn = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Files this turn hands over; a card for each is drawn under its text. */
  files?: BankOutputFile[];
  /** A finished run's progress. `content` then holds its one-line summary. */
  activity?: BankActivity;
};

export type BankSessionState = {
  step: BankRunnerStep;
  turns: BankChatTurn[];
  /** The conversation on the server; null until its first draft was saved. */
  conversationId: string | null;
  /** A picked file stays a `File` only until the next draft is saved; from then on it is the server's. */
  bank: BankUpload | null;
  appendices: BankUpload[];
  analysis: BankAnalysis | null;
  /** The run in progress (a build or a revision), stage by stage; null when none is running. */
  activity: BankActivity | null;
  /** How many versions of the bank the conversation has handed over. */
  version: number;
  /** The agent can carry out an answer to the last version. */
  answerable: boolean;
  /** The lawyer's message a revision that was just asked for carries out (until the server has it). */
  pendingRequest: string | null;
  /** The run is going on in the server (it was started, or the conversation was opened while it ran):
   * the pane follows it, and closing the pane does not end it. */
  following: boolean;
  /** The lawyer pressed stop and the server has not said yet that the run stopped. */
  stopping: boolean;
};

/** One turn as the server keeps it (a chat message of the conversation). */
export type BankServerMessage = {
  id: string;
  content: string;
  is_user: boolean;
  metadata?: {
    kind?: 'text' | 'activity' | 'files' | 'notice';
    warnings?: string[];
    activity?: BankActivity;
    version?: number;
    files?: Array<{ id: string; kind: 'built' | 'refined'; version: number; filename: string; size: number }>;
    notice?: 'stopped' | 'failed';
    run?: 'analyze' | 'build' | 'revise';
  } | null;
};

/** One saved conversation in the list. */
export type BankConversationMeta = {
  id: string;
  title: string;
  updatedAt: number;
  step: BankConversationInfo['step'];
  version: number;
  appendixCount: number;
  /** A run of it is going on in the server right now, and which; the list shows that on its row. */
  running: boolean;
  runningKind: BankServerRun['kind'] | null;
};
